import { AIFI_LEVELS, type AifiLevel } from '@/domains/pages/ai-proficiency/constants';
import {
  formatUserCount,
  formatUserCountPhrase,
  userCountLabel,
} from '@/domains/pages/ai-proficiency/utils/format-users';
import type { LevelTeamBreakdown } from '@/domains/pages/ai-proficiency/utils/map-aggregates';

export type LadderGapTone = 'bad' | 'good';

export type LadderGapMessage = {
  text: string;
  tone: LadderGapTone;
  tooltip: string;
  userDelta: number;
};

export function computeLevelShare(count: number, totalUsers: number): number {
  if (totalUsers <= 0) return 0;
  return (count / totalUsers) * 100;
}

function shareUnitsToPercent(units: number, factor: number, decimals: number): number {
  if (decimals === 0) return units;
  const whole = Math.trunc(units / factor);
  const fraction = units % factor;
  return Number(`${whole}.${String(fraction).padStart(decimals, '0')}`);
}

/**
 * Largest-remainder allocation so rounded shares always sum to exactly 100%
 * at the requested precision (e.g. one decimal place).
 */
export function distributeSharePercents(counts: number[], decimals = 1): number[] {
  const total = counts.reduce((sum, count) => sum + count, 0);
  if (total <= 0) return counts.map(() => 0);

  const factor = 10 ** decimals;
  const targetUnits = 100 * factor;
  const exactUnits = counts.map((count) => (count / total) * targetUnits);
  const flooredUnits = exactUnits.map((value) => Math.floor(value));
  let remainderUnits = targetUnits - flooredUnits.reduce((sum, value) => sum + value, 0);

  const allocationOrder = exactUnits
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((left, right) => right.fraction - left.fraction || left.index - right.index);

  const allocatedUnits = [...flooredUnits];
  for (const { index } of allocationOrder) {
    if (remainderUnits <= 0) break;
    if (counts[index] <= 0) continue;
    allocatedUnits[index] += 1;
    remainderUnits -= 1;
  }

  return allocatedUnits.map((units) => shareUnitsToPercent(units, factor, decimals));
}

/** Level shares that sum to exactly 100% at the given display precision. */
export function levelSharePercentsFromCounts(
  getCount: (level: AifiLevel) => number,
  decimals = 1,
): Record<AifiLevel, number> {
  const counts = AIFI_LEVELS.map(getCount);
  const percents = distributeSharePercents(counts, decimals);
  return Object.fromEntries(AIFI_LEVELS.map((level, index) => [level, percents[index]])) as Record<
    AifiLevel,
    number
  >;
}

/** Headcount with a proficiency tier assigned — used as the denominator for level shares. */
export function sumLevelUserCounts(levels: Array<{ userCount: number }>): number {
  return levels.reduce((sum, level) => sum + level.userCount, 0);
}

export function computeUserGap(
  totalUsers: number,
  currentSharePercent: number,
  topQuartileSharePercent: number,
): number {
  const targetCount = (totalUsers * topQuartileSharePercent) / 100;
  const currentCount = (totalUsers * currentSharePercent) / 100;
  return Math.round(targetCount - currentCount);
}

export function isLevelShareLowerBetter(level: AifiLevel): boolean {
  return level === 'Beginner' || level === 'Novice';
}

/** Selected is best when its share beats the cross-segment benchmark for that level. */
export function isSelectedBestAtLevel(level: AifiLevel, selectedShare: number, bestShare: number): boolean {
  const epsilon = 0.05;
  if (isLevelShareLowerBetter(level)) {
    return selectedShare <= bestShare + epsilon;
  }
  return selectedShare >= bestShare - epsilon;
}

export function isAheadOfLevelBenchmark(level: AifiLevel, selectedShare: number, benchmarkShare: number): boolean {
  if (isLevelShareLowerBetter(level)) {
    return selectedShare <= benchmarkShare;
  }
  return selectedShare >= benchmarkShare;
}

export function computeGapPercent(level: AifiLevel, selectedShare: number, benchmarkShare: number): number {
  const rawGap = selectedShare - benchmarkShare;
  return isLevelShareLowerBetter(level) ? -rawGap : rawGap;
}

export function buildLadderGapMessage(
  level: AifiLevel,
  userDelta: number,
  totalUsers: number,
  topQuartileSharePercent: number,
): LadderGapMessage | null {
  if (userDelta === 0) return null;

  const absDelta = Math.abs(userDelta);
  const targetCount = Math.round((totalUsers * topQuartileSharePercent) / 100);

  if (level === 'Expert' || level === 'Practitioner') {
    if (userDelta > 0) {
      return {
        text: `Bring in ${formatUserCountPhrase(absDelta)}`,
        tone: 'bad',
        tooltip: `Move ${formatUserCount(absDelta)} more ${userCountLabel(absDelta)} into ${level} to reach the top quartile.`,
        userDelta,
      };
    }

    return {
      text: `${formatUserCountPhrase(absDelta)} ahead`,
      tone: 'good',
      tooltip: `${formatUserCount(absDelta)} more ${userCountLabel(absDelta)} in ${level} than the top quartile benchmark.`,
      userDelta,
    };
  }

  // Beginner / Novice — fewer is better
  if (userDelta > 0) {
    return {
      text: `${formatUserCountPhrase(absDelta)} ahead`,
      tone: 'good',
      tooltip: `${formatUserCount(absDelta)} fewer ${userCountLabel(absDelta)} in ${level} than the top quartile benchmark.`,
      userDelta,
    };
  }

  return {
    text: `Move out ${formatUserCountPhrase(absDelta)}`,
    tone: 'bad',
    tooltip: `Move ${formatUserCountPhrase(absDelta)} out of ${level} to match the top quartile (${formatUserCountPhrase(targetCount)}).`,
    userDelta,
  };
}

/** Highest (or lowest for Novice/Beginner) team share at a level, using scored-user denominator. */
export function computeBestTeamShareAtLevel(
  teams: LevelTeamBreakdown[],
  totalUsers: number,
  level: AifiLevel,
): number {
  if (teams.length === 0 || totalUsers <= 0) return 0;

  const isLowerBetter = isLevelShareLowerBetter(level);
  let bestShare = isLowerBetter ? Infinity : -Infinity;

  for (const team of teams) {
    const share = computeLevelShare(team.count, totalUsers);
    const isBetter = isLowerBetter ? share < bestShare : share > bestShare;
    if (isBetter) bestShare = share;
  }

  if (bestShare === Infinity || bestShare === -Infinity) return 0;
  return bestShare;
}

export const OTHERS_TEAM_NAME = 'Others';

const TOP_LADDER_TEAM_COUNT = 4;

/** Top teams by headcount at a ladder level, with the rest folded into Others. */
export function partitionTeamsForLadder(teams: LevelTeamBreakdown[]): LevelTeamBreakdown[] {
  if (teams.length <= TOP_LADDER_TEAM_COUNT) {
    return teams;
  }

  const sorted = [...teams].sort((a, b) => b.count - a.count);
  const top = sorted.slice(0, TOP_LADDER_TEAM_COUNT);
  const othersCount = sorted.slice(TOP_LADDER_TEAM_COUNT).reduce((sum, team) => sum + team.count, 0);

  if (othersCount <= 0) {
    return top;
  }

  return [...top, { team: OTHERS_TEAM_NAME, count: othersCount }];
}
